import type { MouseEvent } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    List,
    ListItem,
    ListItemText,
    Popper,
    Box,
    makeStyles,
    IconButton,
    Typography,
    ClickAwayListener,
} from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { Block, IconName, Illustration } from '@klp/design';
import { contextSelectors } from '@klp/contextSelector';
import { kommentarerSelectorActions } from '../store/kommentarerSlice';
import type { IKommentar } from '../services/rtkApi';
import { useDeleteKommentarMutation } from '../services/rtkApi';

type KommentarPopperProps = {
    kommentar?: IKommentar;
    handleModalCallback: (kommentar?: IKommentar) => void;
};

export const KommentarPopper = ({
    kommentar,
    handleModalCallback,
}: KommentarPopperProps) => {
    const { t } = useTranslation();
    const useStyles = makeStyles(() => ({
        popper: {
            background: 'white',
            border: '1px solid',
            borderRadius: 2,
        },
        redText: {
            color: 'red',
        },
        button: {
            cursor: 'pointer',
            borderRadius: '50%',
            '&:hover': {
                backgroundColor: '#f5f5f5',
                borderRadius: '50%',
            },
        },
    }));
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const dispatch = useDispatch();
    const {
        reducerActions: { setEditMode, setPostMode },
    } = kommentarerSelectorActions;

    const { selectedContext } = useSelector(contextSelectors);

    const [deleteKommentar, { isError: deleteKommentarError }] =
        useDeleteKommentarMutation();

    const handleDeleteKommentar = async () => {
        if (!selectedContext?.id || !kommentar?.id) return;

        await deleteKommentar({
            byggNummer: selectedContext?.id,
            id: kommentar.id,
        });

        if (deleteKommentarError) {
            showToast({
                body: t('contractRegistration.error'),
                type: 'error',
            });
        }
    };

    const handlePopperClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setOpen((prev) => !prev);
        event.stopPropagation();
    };

    const handleClosePopper = () => {
        setOpen(false);
    };

    const handleEditMode = () => {
        if (!kommentar?.id) return;

        dispatch(setEditMode({ editFlag: true, kommentarId: kommentar?.id }));
        dispatch(setPostMode(false));
    };

    const handleOpenDetailedView = (kommentarOpen?: IKommentar) => {
        if (!kommentarOpen?.id) return;

        handleModalCallback(kommentarOpen);
    };

    const canBeOpen = open && Boolean(anchorEl);
    const id = canBeOpen ? 'transition-popper' : undefined;

    return (
        <ClickAwayListener onClickAway={handleClosePopper}>
            <div>
                <IconButton
                    color="inherit"
                    aria-label="Open document view"
                    onClick={handlePopperClick}
                    className={classes.button}
                >
                    <Illustration icon={IconName.OPTIONS} />
                </IconButton>
                <Popper
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    transition={true}
                    style={{ zIndex: 1300 }}
                >
                    <Box className={classes.popper}>
                        <List>
                            <ListItem
                                dense={true}
                                button={true}
                                onClick={() =>
                                    handleOpenDetailedView(kommentar)
                                }
                            >
                                <Block paddingRight="compact">
                                    <Illustration icon={IconName.CIRCLE_INFO} />
                                </Block>
                                <ListItemText>
                                    <Typography>
                                        {t('comments.seeDetails')}
                                    </Typography>
                                </ListItemText>
                            </ListItem>
                            <ListItem
                                dense={true}
                                button={true}
                                onClick={handleEditMode}
                            >
                                <Block paddingRight="compact">
                                    <Illustration icon={IconName.EDIT} />
                                </Block>
                                <ListItemText>
                                    <Typography>
                                        {t('comments.edit')}
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem
                                dense={true}
                                button={true}
                                onClick={() => {
                                    handleClosePopper();
                                    handleDeleteKommentar();
                                }}
                            >
                                <Block paddingRight="compact">
                                    <Illustration icon={IconName.DELETE} />
                                </Block>
                                <ListItemText>
                                    <Typography className={classes.redText}>
                                        {t('comments.delete')}
                                    </Typography>
                                </ListItemText>
                            </ListItem>
                        </List>
                    </Box>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};
